import React, { useLayoutEffect, useRef, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  DEFAULT_NAMESPACE,
  Entity,
  parseEntityRef,
} from '@backstage/catalog-model';
import { DependencyGraphTypes } from '@backstage/core-components';
import { useRouteRef } from '@backstage/core-plugin-api';
import {
  entityRouteRef,
  useEntityPresentation,
} from '@backstage/plugin-catalog-react';
import { isVirtual } from '../../input/VirtualPicker';

/**
 * What a node needs to be drawn: the entity, whether it is the one the page is
 * on, and what a click on it does. The nodes of our relations graph and the
 * ones of the catalog graph plugin both carry it.
 */
export type EntityGraphNodeData = {
  id: string;
  entity: Entity;
  focused?: boolean;
  onClick?: (event: React.MouseEvent<unknown>) => void;
};

const useNodeStyles = makeStyles(
  theme => ({
    node: {
      fill: theme.palette.grey[300],
      stroke: theme.palette.grey[300],
      '&.primary': {
        fill: theme.palette.primary.light,
        stroke: theme.palette.primary.light,
      },
      '&.secondary': {
        fill: theme.palette.secondary.light,
        stroke: theme.palette.secondary.light,
      },
      // A dashed outline in the darker shade of the fill, so that the box
      // still reads as the colour it is and the text on it stays legible.
      '&.virtual': {
        strokeWidth: 2,
        strokeDasharray: '6 3',
      },
      '&.virtual.primary': {
        stroke: theme.palette.primary.dark,
      },
      '&.virtual.secondary': {
        stroke: theme.palette.secondary.dark,
      },
    },
    text: {
      fill: theme.palette.getContrastText(theme.palette.grey[300]),
      '&.primary': {
        fill: theme.palette.primary.contrastText,
      },
      '&.secondary': {
        fill: theme.palette.secondary.contrastText,
      },
      '&.focused': {
        fontWeight: 'bold',
      },
    },
    clickable: {
      cursor: 'pointer',
    },
  }),
  { name: 'RtlDataRelationsGraphNode' },
);

/**
 * Whether a click is one the browser has a meaning for on a link - a new tab,
 * a new window, a download - rather than a plain one.
 */
function isModifiedClick(event: React.MouseEvent<unknown>): boolean {
  return (
    event.button !== 0 ||
    event.metaKey ||
    event.ctrlKey ||
    event.shiftKey ||
    event.altKey
  );
}

/**
 * An entity as a rounded box with the icon and the title it is presented with
 * everywhere else.
 *
 * The box is a link to the entity page. A plain click does what the graph
 * does with it - open the entity without leaving the app, or on the catalog
 * graph page centre the graph on it - and every other click is left to the
 * browser: with a modifier key or the middle button, or the "Open link in new
 * tab" of the context menu, the entity opens in a tab of its own. The graph
 * draws an svg, so without the link a node was a shape with a click handler
 * that no browser knew how to open anywhere else.
 */
export function EntityGraphNode({
  node,
}: DependencyGraphTypes.RenderNodeProps<EntityGraphNodeData>) {
  const classes = useNodeStyles();
  const entityRoute = useRouteRef(entityRouteRef);
  const presentation = useEntityPresentation(node.entity, {
    defaultNamespace: DEFAULT_NAMESPACE,
  });
  const textRef = useRef<SVGTextElement>(null);
  const [{ width, height }, setSize] = useState({ width: 0, height: 0 });

  // The box is drawn around the text, so it can only be sized once the text
  // has been laid out.
  useLayoutEffect(() => {
    if (!textRef.current) {
      return;
    }
    const box = textRef.current.getBBox();
    const textWidth = Math.round(box.width);
    const textHeight = Math.round(box.height);
    if (textWidth !== width || textHeight !== height) {
      setSize({ width: textWidth, height: textHeight });
    }
  }, [width, height]);

  const { kind, namespace, name } = parseEntityRef(node.id);
  const href = entityRoute({
    kind: kind.toLocaleLowerCase('en-US'),
    namespace: namespace.toLocaleLowerCase('en-US'),
    name,
  });
  const onClick = (event: React.MouseEvent<unknown>) => {
    // Without a handler of its own the node is an ordinary link.
    if (!node.onClick || isModifiedClick(event)) {
      return;
    }
    event.preventDefault();
    node.onClick(event);
  };

  // The presentation hands the icon over as a component that takes nothing but
  // a font size, while it is drawn here as an element of the surrounding svg.
  const Icon = presentation.Icon as unknown as
    | React.ComponentType<React.SVGProps<SVGSVGElement>>
    | undefined;
  const padding = 10;
  const iconArea = Icon ? height + padding : 0;
  const boxWidth = iconArea + width + padding * 2;
  const boxHeight = height + padding * 2;
  // The entity the page is on stands out from the ones around it, the same way
  // the catalog graph draws it.
  const color = node.focused ? 'secondary' : 'primary';
  const text = `${classes.text} ${color} ${node.focused ? 'focused' : ''}`;
  // A team or a department outside the line organisation is drawn with a
  // dashed outline, the way the About card outlines its Virtual chip.
  const virtual = isVirtual(node.entity);

  return (
    <a href={href} onClick={onClick}>
      <g className={classes.clickable}>
        <rect
          className={`${classes.node} ${color}${virtual ? ' virtual' : ''}`}
          width={boxWidth}
          height={boxHeight}
          rx={10}
        />
        {Icon && (
          <Icon
            className={text}
            x={padding}
            y={padding}
            width={height}
            height={height}
            style={{ width: height, height, fontSize: height }}
          />
        )}
        <text
          ref={textRef}
          className={text}
          x={iconArea + (width + padding * 2) / 2}
          y={boxHeight / 2}
          textAnchor="middle"
          alignmentBaseline="middle"
        >
          {presentation.primaryTitle}
        </text>
        <title>{virtual ? `${node.id} (virtual)` : node.id}</title>
      </g>
    </a>
  );
}
